<template>
  <div class="login-container">
    <div class="login-shell">
      <div ref="imgRef" class="login-visual">
        <div class="visual-green-mask" />
        <el-carousel :height="carouselHeight" class="visual-carousel" trigger="click">
          <el-carousel-item v-for="item in carouselArr" :key="item.backgroundImage">
            <div :style="item" class="carousel-img" />
          </el-carousel-item>
        </el-carousel>
        <div class="visual-copy">
          <span class="visual-badge">ANJUN EXPRESS</span>
          <p class="visual-kicker">SMART LOGISTICS WORKSPACE</p>
          <h1 class="visual-title">{{ $t('login.anjunKD') }}</h1>
          <p class="visual-desc">{{ $t('login.WELCOME') }}</p>
          <div class="visual-metrics">
            <div class="metric-card">
              <span class="metric-label">Routes</span>
              <strong>Global Delivery</strong>
            </div>
            <div class="metric-card">
              <span class="metric-label">Tracking</span>
              <strong>Realtime Visibility</strong>
            </div>
            <div class="metric-card">
              <span class="metric-label">Network</span>
              <strong>Warehouse Matrix</strong>
            </div>
          </div>
        </div>
      </div>

      <div class="login-panel">
        <div class="login-box">
          <div class="login-toolbar">
            <span class="login-toolbar__label">Language</span>
            <lang-select class="login-language-select" />
          </div>

          <div class="login-brand">
            <span class="brand-mark" />
            <span class="brand-name">{{ $t('login.anjunKD') }}</span>
          </div>

          <div class="title">
            <p class="title-main">{{ $t('login.WELCOME') }}</p>
            <p class="title-sub">{{ $t('login.platformSubtitle') }}</p>
          </div>

          <div class="inp_box">
            <div class="field">
              <span class="field-label">{{ $t('login.inputNumber') }}</span>
              <div class="field-control">
                <i class="el-icon-user" />
                <input
                  v-model.trim="loginForm.username"
                  :placeholder="$t('login.inputNumber')"
                  autofocus
                  max="20"
                  min="3"
                  type="text"
                  @keyup.enter.prevent="handleLogin"
                >
              </div>
            </div>

            <div class="field field-password">
              <span class="field-label">{{ $t('login.passwordPrompt') }}</span>
              <div class="field-control">
                <i class="el-icon-lock" />
                <input
                  v-model.trim="loginForm.password"
                  :placeholder="$t('login.passwordPrompt')"
                  max="20"
                  min="3"
                  :type="passwordType"
                  @keyup.enter.prevent="handleLogin"
                >
                <button
                  :aria-label="passwordType === 'password' ? '显示密码' : '隐藏密码'"
                  class="password-visibility-toggle"
                  type="button"
                  @click="togglePasswordVisible"
                >
                  <i class="el-icon-view" :class="{ 'is-password-hidden': passwordType === 'password' }" />
                </button>
              </div>
            </div>
          </div>

          <div id="recaptcha-container" class="recaptcha-container" />

          <div class="but">
            <button type="button" @click="handleLogin">
              <span>{{ $t('login.login') }}</span>
              <i class="el-icon-right" />
            </button>
          </div>

          <div class="login-divider">
            <span>{{ $t('login.enterpriseLoginDivider') }}</span>
          </div>

          <div class="feishu-login">
            <div class="feishu-login-content" @click="handleFeishuLogin">
              <img alt="飞书登录" class="feishu-login-icon" src="/static/logo/feishu-logo.png">
              <span class="feishu-login-text">{{ $t('login.feishuLogin') }}</span>
              <i class="el-icon-right feishu-login-arrow" />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { removeToken } from '@/utils/auth';
// import { setRSAEncrypt } from '@/utils/RSA_util';
import { MessageBox } from 'element-ui';
import { getUserIpInformation } from '@/api/user';
import { computer } from '@/utils/current_browser_util';
import { Base64 } from 'js-base64';
import md5 from 'js-md5';
import { loadGoogleCaptchaV2Script } from '@/utils/captchaUtils';
import LangSelect from '@/components/LangSelect/index.vue';

export default {
  name: 'Login',
  components: { LangSelect },
  data() {
    return {
      loginForm: {
        username: '',
        password: ''
      },
      passwordType: 'password',
      redirect: undefined,

      carouselArr: [
        {
          backgroundImage: 'url(' + require('@/assets/images/carousel/1.jpg') + ')'
        },
        {
          backgroundImage: 'url(' + require('@/assets/images/carousel/2.jpg') + ')'
        },
        {
          backgroundImage: 'url(' + require('@/assets/images/carousel/3.jpg') + ')'
        },
        {
          backgroundImage: 'url(' + require('@/assets/images/carousel/4.jpg') + ')'
        },
        {
          backgroundImage: 'url(' + require('@/assets/images/carousel/5.jpg') + ')'
        },

        {
          backgroundImage: 'url(' + require('@/assets/images/carousel/6.jpg') + ')'
        }
      ],
      carouselHeight: '', // 轮播图高度
      siteKey: '', // 验证码key
      v2Token: '', // v2验证token
      isV2Recaptcha: false, // 是否加载v2验证码
      feishuAppId: process.env.VUE_APP_FEISHU_APP_ID || '', // 飞书应用ID
      feishuRedirectUri: process.env.VUE_APP_FEISHU_REDIRECT_URI || `${window.location.origin}`, // 飞书重定向URI
      loginPending: false // 登录防抖
    };
  },

  watch: {
    $route: {
      handler: function(route) {
        this.redirect = route.query && route.query.redirect;
      },
      immediate: true
    }
  },

  created() {
    const goURLStr = this.goHTTPS();
    this.siteKey = process.env.VUE_APP_RECAPTCHA_SITE_KEY_V2;
    goURLStr && window.location.replace(goURLStr);

    // 检查是否是从飞书授权回调
    const query = this.$route.query;
    if (query.code) {
      // 使用飞书授权码登录
      this.handleFeishuCallback(query.code);
    }
  },

  mounted() {
    // 绑定enter事件
    this.enterKeyup();
    this.carouselHeight = this.getImgsHeight(this.$refs.imgRef);
    window.addEventListener('resize', () => {
      this.carouselHeight = this.getImgsHeight(this.$refs.imgRef);
    });
    if (process.env.NODE_ENV === 'production') {
      loadGoogleCaptchaV2Script().then(() => {
        this.$nextTick(() => {
          this.isV2Recaptcha = true;
          window.grecaptcha.render('recaptcha-container', {
            sitekey: process.env.VUE_APP_RECAPTCHA_SITE_KEY_V2,
            callback: (token) => {
              console.error('✅ v2 token 获取成功');
              this.v2Token = token;
            },
            'error-callback': (error) => {
              console.error('❌ v2 验证失败:', error);
              this.v2Token = '';
            },
            'expired-callback': () => {
              console.warn('⚠️ v2 验证已过期');
              this.v2Token = '';
            }
          });
        });
      });
    }
  },

  destroyed() {
    // 销毁enter事件
    this.enterKeyupDestroyed();
  },
  methods: {
    // 检测是否是正式环境
    /**
     * 检测如果是正式环境，访问的是http（非https）
     * 返回https的地址
     */
    goHTTPS() {
      const ENV = process.env.ENV;
      // console.log(ENV);
      const href = window.location.href;

      // 非正式环境
      if (ENV !== 'production') {
        return '';
      }

      // 正式环境
      const { protocol, pathname, host } = new URL(href);

      // 当前已经是https了
      if (protocol === 'https:') {
        return '';
      }
      // 强制转https
      return `https://${host}${pathname}`;
    },

    /**
     * 注册
     */
    enterKeyup() {
      document.addEventListener('keyup', this.enterKey);
    },

    /**
     * 销毁
     */
    enterKeyupDestroyed() {
      document.removeEventListener('keyup', this.enterKey);
    },

    /**
     * 在当前页面监听回车键
     */
    enterKey(event) {
      const componentName = this.$options.name;
      if (componentName !== 'Login') {
        return;
      }
      if (event.key !== 'Enter') {
        return;
      }
      this.handleLogin();
    },

    // 账户密码 简单检测
    checkInut() {
      const { username, password } = this.loginForm;

      if (!username.length) {
        this.$message({
          message: this.$t('login.inputNumber'),
          type: 'warning'
        });
        return false;
      }
      if (!password.length) {
        this.$message({
          message: this.$t('login.passwordPrompt'),
          type: 'warning'
        });
        return false;
      }
      return true;
    },
    togglePasswordVisible() {
      this.passwordType = this.passwordType === 'password' ? 'text' : 'password';
    },
    /**
     * @description 谷歌验证码v2成功的token
     */
    handleLogin() {
      // 请求还没结束,直接拦截,不需要用定时器去"猜"什么时候解锁
      if (this.loginPending) {
        return;
      }

      let { password } = this.loginForm;
      password = md5(md5(password));
      const loginForm = {
        username: this.loginForm.username,
        password: password
      };
      if (this.v2Token) {
        loginForm.v2Token = this.v2Token;
      } else {
        loginForm.v2Token = '';
      }

      const isBool = this.checkInut();
      if (!isBool) {
        return; // 校验失败,loginPending还没置true，不需要重置
      }
      if (this.isV2Recaptcha && this.v2Token === '' && process.env.NODE_ENV === 'production') {
        this.$message({
          message: this.$t('login.verifyBeforeLogin'),
          type: 'warning'
        });
        return; // 同上，校验失败提前拦截，loginPending还没置true
      }

      // 所有前置校验都通过，才真正加锁，开始请求
      this.loginPending = true;

      this.$store.state.tagsView.cachedViews = [];
      this.$store.state.tagsView.visitedViews = [];
      this.$store
        .dispatch('user/login', loginForm)
        .then((data) => {
          const overTime = new Date().getTime() + 12 * 60 * 60 * 1000;
          sessionStorage.setItem('overTime', overTime);
          this.throttling(12 * 60 * 60 * 1000);
          this.getUserIpInformationData();
        })
        .then((res) => {
          if (this.loginForm.password === '123456' || this.loginForm.password === 'anjun123') {
            MessageBox.confirm(this.$t('login.pwdTipContent'), this.$t('login.pwdTipTop'), {
              confirmButtonText: this.$t('login.confirm'),
              type: 'warning',
              center: true,
              showClose: false
            })
              .then((arr) => {
                this.$router.push({ path: '/profile' });
              })
              .catch(() => {
                this.$router.push({ path: '/' });
              });
          } else {
            this.$router.push({ path: '/' });
          }
        })
        .catch((err) => {
          console.log(err);
        })
        .finally(() => {
          // 无论成功还是失败，请求真正结束后才解锁——跟接口耗时完全同步
          this.loginPending = false;
        });
    },
    // 定时退出登录
    throttling(wait) {
      // console.log('等待时长：', wait);
      const thatt = this;
      let tiems = 0;
      const now = new Date().getTime();
      if (now - tiems > wait) {
        tiems = setTimeout(function() {
          // console.log('已到时');
          removeToken();
          thatt.$router.push({ path: '/login' });
          clearTimeout(tiems);
          return;
        }, wait);
      }
    },
    // 登录日志信息
    getUserIpInformationData() {
      const userToken = this.$store.state.user.token.split('.')[1];
      const userStr = Base64.decode(userToken); // window.atob(userToken);
      const user = JSON.parse(userStr);
      const getIp = sessionStorage.getItem('ip');
      const getIpAddress = sessionStorage.getItem('ipAddress');
      const param = {
        browserInfo: computer.browserInfo,
        createUser: user.id,
        // ip: returnCitySN.cip,
        ip: getIp,
        // ipAddress: returnCitySN.cname,
        ipAddress: getIpAddress,
        osInfo: computer.osInfo,
        source: '',
        username: user.realName
      };
      return getUserIpInformation(param);
    },

    /**
     * 获取高度
     */
    getImgsHeight(doms) {
      return window.getComputedStyle(doms, null).height;
    },

    /**
     * 飞书登录
     */
    handleFeishuLogin() {
      if (!this.feishuAppId) {
        this.$message({
          message: this.$t('login.feishuAppIdNotConfig'),
          type: 'error'
        });
        return;
      }

      // 构建飞书授权URL
      // 使用配置的重定向URI，如果没有配置则使用当前域名
      const redirectUri = encodeURIComponent(`${this.feishuRedirectUri}/login`);
      const state = encodeURIComponent('feishu_login');
      const scope = 'contact:contact.base:readonly';

      const feishuAuthUrl = `https://accounts.feishu.cn/open-apis/authen/v1/authorize?client_id=${this.feishuAppId}&response_type=code&redirect_uri=${redirectUri}&scope=${scope}&state=${state}`;

      // 跳转到飞书授权页面
      window.location.href = feishuAuthUrl;
    },

    /**
     * 处理飞书授权回调
     */
    async handleFeishuCallback(code) {
      try {
        this.$message({
          message: this.$t('login.loggingIn'),
          type: 'info'
        });

        // 清空缓存的视图
        this.$store.state.tagsView.cachedViews = [];
        this.$store.state.tagsView.visitedViews = [];
        // 调用飞书登录 action
        await this.$store.dispatch('user/feishuLogin', code);
        // 设置超时时间
        const overTime = new Date().getTime() + 12 * 60 * 60 * 1000;
        sessionStorage.setItem('overTime', overTime);
        // 启动定时器
        this.throttling(12 * 60 * 60 * 1000);
        // 获取用户信息
        const { permission } = await this.$store.dispatch('user/getInfo');
        // 根据角色生成可访问的路由
        const accessRoutes = await this.$store.dispatch(
          'permission/generateRoutes',
          permission.menuPermissions
        );
        // 动态添加可访问的路线
        this.$router.addRoutes(accessRoutes);
        // 记录IP信息
        this.getUserIpInformationData();
        // 登录成功，跳转到首页
        this.$message({
          message: this.$t('login.loginSuccess'),
          type: 'success'
        });

        this.$router.push({ path: '/' });
      } catch (error) {
        console.error('飞书登录失败:', error);

        // 检查是否是 code 301 错误（未绑定账号）
        // 需要处理多种可能的错误结构
        let bindVerify = null;
        let errorMsg = this.$t('login.feishuLoginFailed');

        if (error) {
          if (error.response && error.response.data) {
            // axios 错误响应
            if (error.response.data.code === 301) {
              bindVerify = error.response.data.data;
              errorMsg = error.response.data.msg || this.$t('login.accountBindRequired');
            } else {
              errorMsg = error.response.data.msg || errorMsg;
            }
          } else if (error.code === 301) {
            // 其他可能的错误结构
            bindVerify = error.data;
            errorMsg = error.msg || this.$t('login.accountBindRequired');
          } else {
            errorMsg = error.message || errorMsg;
          }
        }

        if (bindVerify) {
          // 跳转到绑定页面，传递 bindVerify 参数
          this.$router.push({
            path: '/feishu-bind',
            query: { bindVerify }
          });
        } else {
          this.$message({
            message: errorMsg,
            type: 'error'
          });
        }
      }
    }
  }
};
</script>

<style lang="scss">
.login-container {
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-text-fill-color: #1f2937 !important;
    box-shadow: 0 0 0 1000px #fff inset !important;
    transition: background-color 9999s ease-out 0s;
  }
}
</style>

<style lang="scss" scoped>
p {
  margin: 0;
}

.login-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  overflow-x: hidden;
  overflow-y: auto;
  background:
    radial-gradient(circle at 12% 18%, rgba(0, 165, 81, 0.22), transparent 32%),
    radial-gradient(circle at 88% 16%, rgba(66, 195, 126, 0.18), transparent 24%),
    linear-gradient(135deg, #f4fbf6 0%, #e7f7ee 48%, #edf6f0 100%);

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.6) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.6) 1px, transparent 1px);
    background-size: 72px 72px;
    opacity: 0.45;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    right: -120px;
    bottom: -180px;
    width: 420px;
    height: 420px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(0, 165, 81, 0.22) 0%, rgba(0, 165, 81, 0) 72%);
    pointer-events: none;
  }
}

.login-shell {
  position: relative;
  z-index: 1;
  display: flex;
  width: 100%;
  max-width: 1180px;
  min-height: 680px;
  padding: 24px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 32px;
  background: rgba(255, 255, 255, 0.4);
  box-shadow: 0 32px 80px rgba(16, 64, 43, 0.14);
  backdrop-filter: blur(18px);
}

.login-visual {
  position: relative;
  flex: 1.08;
  min-height: 632px;
  border-radius: 28px;
  overflow: hidden;
  background: #111827;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 3;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.14) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.14) 1px, transparent 1px);
    background-size: 38px 38px;
    opacity: 0.65;
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 4;
    background:
      radial-gradient(circle at 18% 22%, rgba(255, 255, 255, 0.14), transparent 28%),
      linear-gradient(180deg, rgba(17, 24, 39, 0.06) 0%, rgba(17, 24, 39, 0.44) 100%);
    pointer-events: none;
  }
}

.visual-green-mask {
  position: absolute;
  inset: 0;
  z-index: 2;
  background:
    radial-gradient(circle at 18% 22%, rgba(255, 255, 255, 0.12), transparent 28%),
    linear-gradient(160deg, rgba(16, 183, 97, 0.62) 0%, rgba(11, 152, 88, 0.58) 48%, rgba(13, 118, 80, 0.64) 100%);
  pointer-events: none;
}

.visual-carousel {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.visual-copy {
  position: relative;
  z-index: 5;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 48px;
  color: #fff;
  pointer-events: none;
}

.visual-badge {
  display: inline-flex;
  align-items: center;
  width: fit-content;
  padding: 10px 16px;
  border: 1px solid rgba(255, 255, 255, 0.26);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  backdrop-filter: blur(12px);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.18em;
}

.visual-kicker {
  margin-top: auto;
  font-size: 13px;
  letter-spacing: 0.22em;
  color: rgba(255, 255, 255, 0.78);
}

.visual-title {
  max-width: 520px;
  margin: 18px 0 12px;
  font-size: 54px;
  line-height: 1.08;
  font-weight: 700;
}

.visual-desc {
  max-width: 420px;
  font-size: 18px;
  line-height: 1.7;
  color: rgba(255, 255, 255, 0.84);
}

.visual-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-top: 36px;
}

.metric-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 18px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 20px;
  background: rgba(7, 44, 28, 0.18);
  backdrop-filter: blur(14px);

  strong {
    font-size: 18px;
    line-height: 1.4;
    font-weight: 600;
  }
}

.metric-label {
  font-size: 12px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.68);
}

.login-panel {
  display: flex;
  flex: 0.92;
  align-items: center;
  justify-content: center;
  padding: 8px 0 8px 24px;
}

.login-box {
  width: 100%;
  max-width: 430px;
  padding: 40px 38px 36px;
  border: 1px solid rgba(7, 132, 72, 0.08);
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 24px 64px rgba(14, 66, 42, 0.14);
}

.login-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-bottom: 18px;
}

.login-toolbar__label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #8aa89a;
}

.login-language-select {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: 1px solid rgba(0, 165, 81, 0.14);
  border-radius: 14px;
  background: #f3fbf6;
  color: #00a551;
  cursor: pointer;
  transition: all 0.22s ease;

  &:hover {
    border-color: rgba(0, 165, 81, 0.28);
    background: #e8f8ef;
    box-shadow: 0 12px 26px rgba(0, 165, 81, 0.1);
    transform: translateY(-1px);
  }
}

::v-deep .login-language-select,
::v-deep .login-language-select > div {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

::v-deep .login-language-select svg {
  width: 22px;
  height: 22px;
}

.login-brand {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  border-radius: 999px;
  background: #f3fbf6;
  color: #0d8d52;
}

.brand-mark {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: linear-gradient(135deg, #31c874 0%, #00a551 100%);
  box-shadow: 0 0 0 6px rgba(0, 165, 81, 0.12);
}

.brand-name {
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.04em;
}

.title {
  margin-top: 28px;
}

.title-main {
  font-size: 36px;
  line-height: 1.2;
  font-weight: 700;
  color: #1e293b;
}

.title-sub {
  margin-top: 10px;
  font-size: 14px;
  line-height: 1.7;
  color: #64748b;
}

.inp_box {
  margin-top: 32px;
}

.field + .field {
  margin-top: 18px;
}

.field-label {
  display: block;
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
}

.field-control {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 16px;
  border: 1px solid transparent;
  border-radius: 18px;
  background: #fff;
  box-shadow: inset 0 0 0 1px rgba(15, 118, 64, 0.08);
  transition: all 0.25s ease;

  i {
    font-size: 18px;
    color: #8aa89a;
  }

  &:hover,
  &:focus-within {
    border-color: rgba(0, 165, 81, 0.28);
    box-shadow:
      inset 0 0 0 1px rgba(0, 165, 81, 0.16),
      0 12px 24px rgba(0, 165, 81, 0.08);

    i {
      color: #00a551;
    }
  }

  input {
    width: 100%;
    min-width: 0;
    height: 54px;
    border: 0;
    padding: 0;
    background: transparent;
    outline: none;
    color: #1f2937;
    font-size: 15px;
  }

  input::placeholder {
    color: #94a3b8;
  }
}

.password-visibility-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 34px;
  height: 34px;
  margin-right: -6px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: #8aa89a;
  cursor: pointer;
  transition: all 0.2s ease;

  i {
    font-size: 17px;
    color: currentColor;
  }

  .el-icon-view {
    position: relative;

    &.is-password-hidden::after {
      content: '';
      position: absolute;
      left: 50%;
      top: 50%;
      width: 18px;
      height: 2px;
      border-radius: 999px;
      background: currentColor;
      transform: translate(-50%, -50%) rotate(-42deg);
      box-shadow: 0 0 0 2px #fff;
    }
  }

  &:hover,
  &:focus {
    outline: none;
    background: #e8f8ef;
    color: #00a551;
  }
}

.recaptcha-container {
  margin-top: 20px;
  min-height: 78px;
}

.but {
  margin-top: 20px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    height: 52px;
    border: 0;
    border-radius: 18px;
    background: linear-gradient(135deg, #1cc46c 0%, #00a551 100%);
    box-shadow: 0 16px 32px rgba(0, 165, 81, 0.24);
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.25s ease, box-shadow 0.25s ease;
  }

  button:hover {
    transform: translateY(-1px);
    box-shadow: 0 18px 36px rgba(0, 165, 81, 0.28);
  }

  button:focus {
    outline: none;
  }
}

.login-divider {
  position: relative;
  margin: 28px 0 18px;
  text-align: center;

  &::before {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    top: 50%;
    height: 1px;
    background: linear-gradient(90deg, rgba(148, 163, 184, 0) 0%, rgba(148, 163, 184, 0.7) 50%, rgba(148, 163, 184, 0) 100%);
  }

  span {
    position: relative;
    display: inline-block;
    padding: 0 12px;
    background: #fff;
    color: #94a3b8;
    font-size: 12px;
  }
}

.feishu-login-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: #f8fbf9;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    border-color: rgba(0, 165, 81, 0.22);
    box-shadow: 0 14px 30px rgba(15, 118, 64, 0.08);
    transform: translateY(-1px);
  }
}

.feishu-login-icon {
  width: 32px;
  height: 32px;
  padding: 4px;
  border-radius: 10px;
  background: #fff;
  object-fit: contain;
  box-shadow: 0 8px 18px rgba(148, 163, 184, 0.16);
}

.feishu-login-text {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

.feishu-login-arrow {
  color: #00a551;
  font-size: 14px;
}

.carousel-img {
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  opacity: 1;
  transform: scale(1.02);
  filter: saturate(1) brightness(0.94);
}

::v-deep .visual-carousel,
::v-deep .visual-carousel .el-carousel__container,
::v-deep .visual-carousel .el-carousel__item {
  height: 100%;
}

::v-deep .visual-carousel .el-carousel__indicators {
  bottom: 18px;
}

::v-deep .visual-carousel .el-carousel__button {
  width: 18px;
  height: 4px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.56);
}

@media (max-width: 1080px) {
  .login-shell {
    min-height: 620px;
    padding: 18px;
  }

  .visual-copy {
    padding: 40px;
  }

  .visual-title {
    font-size: 44px;
  }

  .visual-metrics {
    grid-template-columns: 1fr;
  }

  .login-panel {
    padding-left: 18px;
  }
}

@media (max-height: 760px) and (min-width: 901px) {
  .login-container {
    align-items: flex-start;
    padding: 16px;
  }

  .login-shell {
    min-height: auto;
    max-height: calc(100vh - 32px);
    padding: 18px;
  }

  .login-visual {
    min-height: auto;
  }

  .visual-copy {
    padding: 32px;
  }

  .visual-title {
    font-size: 40px;
  }

  .visual-desc {
    font-size: 16px;
    line-height: 1.55;
  }

  .visual-metrics {
    gap: 10px;
    margin-top: 24px;
  }

  .metric-card {
    gap: 6px;
    padding: 14px;
  }

  .login-box {
    padding: 28px 32px 24px;
  }

  .login-toolbar {
    margin-bottom: 14px;
  }

  .title {
    margin-top: 22px;
  }

  .title-main {
    font-size: 32px;
  }

  .inp_box {
    margin-top: 24px;
  }

  .field + .field {
    margin-top: 14px;
  }

  .field-label {
    margin-bottom: 8px;
  }

  .field-control input,
  .but button {
    height: 48px;
  }

  .recaptcha-container {
    margin-top: 16px;
    min-height: 64px;
  }

  .but {
    margin-top: 16px;
  }

  .login-divider {
    margin: 20px 0 16px;
  }

  .feishu-login-content {
    padding: 12px 14px;
  }
}

@media (max-width: 900px) {
  .login-container {
    padding: 16px;
  }

  .login-shell {
    min-height: auto;
    padding: 0;
    background: rgba(255, 255, 255, 0.56);
  }

  .login-visual {
    display: none;
  }

  .login-panel {
    flex: 1;
    padding: 20px;
  }

  .login-box {
    max-width: none;
    padding: 32px 24px;
  }
}

@media (max-width: 480px) {
  .login-container {
    padding: 12px;
  }

  .login-panel {
    padding: 12px;
  }

  .login-box {
    padding: 28px 18px 24px;
    border-radius: 24px;
  }

  .title-main {
    font-size: 30px;
  }

  .title-sub {
    font-size: 13px;
  }

  .field-control {
    padding: 0 14px;
    border-radius: 16px;
  }

  .field-control input,
  .but button {
    height: 50px;
  }

  .feishu-login-content {
    padding: 13px 14px;
  }
}
</style>
